import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  IconButton,
  Button,
  Chip,
  Avatar,
  Tooltip,
  Menu,
  MenuItem,
  Badge,
  alpha,
  useTheme,
  useMediaQuery,
  Fab,
  Stack,
  SwipeableDrawer,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Clock,
  CalendarDays,
  Calendar,
  List,
  Bug,
  Lightbulb,
  Zap,
  HelpCircle,
  FileText,
  Layers,
  Lock,
  ArrowUpRight,
  Link2,
  Repeat,
  ChevronRight as ChevronRightIcon,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';

import { useAppDispatch, useAppSelector } from '@/store';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { openModal } from '@/store/slices/uiSlice';
import { taskService, leaveService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { Task, TaskStatus, TaskPriority, LeaveRequest, LeaveStatus, IssueType } from '@/types';
import PriorityBadge from '@/shared/components/PriorityBadge';
import { getCategoryColor } from '@/shared/utils/categoryStyles';

// Issue type config
const issueTypeConfig: Record<IssueType, { icon: React.FC<any>; color: string; label: string }> = {
  bug: { icon: Bug, color: '#dc2626', label: 'Bug' },
  feature: { icon: Lightbulb, color: '#16a34a', label: 'Feature' },
  improvement: { icon: Zap, color: '#2563eb', label: 'Improvement' },
  task: { icon: FileText, color: '#6b7280', label: 'Task' },
  question: { icon: HelpCircle, color: '#9333ea', label: 'Question' },
};

type ViewMode = 'month' | 'week';

/**
 * CalendarPage
 * Calendar view for tasks with month/week views
 * Deadline-centric planning interface
 * Mobile-first responsive design
 */
const CalendarPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector(selectCurrentUser);
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [mobileTaskDrawer, setMobileTaskDrawer] = useState<{ open: boolean; date: Dayjs | null; tasks: Task[] }>({ open: false, date: null, tasks: [] });
  const [showRecurring, setShowRecurring] = useState(true);

  // Fetch tasks
  const { data: tasksData } = useQuery({
    queryKey: QUERY_KEYS.tasks,
    queryFn: () => taskService.getTasks(),
  });

  const tasks = tasksData?.items ?? [];

  // Create task lookup for quick access to parent tasks
  const taskLookup = useMemo(() => {
    const lookup = new Map<string, Task>();
    tasks.forEach((task: Task) => lookup.set(task.id, task));
    return lookup;
  }, [tasks]);

  // Fetch leave requests from API
  const { data: leaveResponse } = useQuery({
    queryKey: [...QUERY_KEYS.calendarEvents, currentDate.format('YYYY-MM')],
    queryFn: () => {
      const startOfMonth = currentDate.startOf('month').toISOString();
      const endOfMonth = currentDate.endOf('month').toISOString();
      return leaveService.getLeaveCalendar(startOfMonth, endOfMonth);
    },
  });
  const leaveRequests: LeaveRequest[] = leaveResponse?.data ?? [];

  // Get tasks for a specific date
  const getTasksForDate = (date: Dayjs): Task[] => {
    const filteredTasks = tasks.filter((task: Task) =>
      dayjs(task.dueDate).isSame(date, 'day')
    );
    
    // Apply recurring filter if needed
    if (!showRecurring) {
      return filteredTasks.filter((task: Task) => !task.isRecurring);
    }
    
    return filteredTasks;
  };

  // Get leave requests for a specific date
  const getLeavesForDate = (date: Dayjs): LeaveRequest[] => {
    return leaveRequests.filter((leave: LeaveRequest) => {
      const leaveStart = dayjs(leave.startDate);
      const leaveEnd = dayjs(leave.endDate);
      return (
        (date.isAfter(leaveStart, 'day') || date.isSame(leaveStart, 'day')) &&
        (date.isBefore(leaveEnd, 'day') || date.isSame(leaveEnd, 'day'))
      );
    });
  };

  // Navigation
  const handlePrev = () => {
    if (viewMode === 'month') {
      setCurrentDate(currentDate.subtract(1, 'month'));
    } else {
      setCurrentDate(currentDate.subtract(1, 'week'));
    }
  };

  const handleNext = () => {
    if (viewMode === 'month') {
      setCurrentDate(currentDate.add(1, 'month'));
    } else {
      setCurrentDate(currentDate.add(1, 'week'));
    }
  };

  const handleToday = () => {
    setCurrentDate(dayjs());
  };

  const handleDateClick = (date: Dayjs, event: React.MouseEvent<HTMLElement>) => {
    setSelectedDate(date);
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedDate(null);
  };

  const handleCreateTask = () => {
    const dueDate = selectedDate?.format('YYYY-MM-DD');
    dispatch(openModal({
      type: 'taskCreate',
      data: dueDate ? { dueDate } : {},
    }));
    handleMenuClose();
  };

  const handleViewAllTasks = () => {
    handleMenuClose();
    navigate('/tasks');
  };

  const handleTaskClick = (taskId: string) => {
    navigate(`/tasks?taskId=${taskId}`);
  };

  // Generate calendar days for month view
  const getMonthDays = () => {
    const startOfMonth = currentDate.startOf('month');
    const endOfMonth = currentDate.endOf('month');
    const startDay = startOfMonth.day();
    const daysInMonth = currentDate.daysInMonth();

    const days: (Dayjs | null)[] = [];

    // Add empty slots for days before month starts
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(startOfMonth.date(i));
    }

    return days;
  };

  // Generate week days
  const getWeekDays = () => {
    const startOfWeek = currentDate.startOf('week');
    const days: Dayjs[] = [];

    for (let i = 0; i < 7; i++) {
      days.push(startOfWeek.add(i, 'day'));
    }

    return days;
  };

  // Get priority color
  const getPriorityColor = (priority: TaskPriority) => {
    switch (priority) {
      case TaskPriority.P1:
        return '#ef4444';
      case TaskPriority.P2:
        return '#f59e0b';
      case TaskPriority.P3:
        return '#2563eb';
      case TaskPriority.P4:
        return '#94a3b8';
      default:
        return '#64748b';
    }
  };

  const weekDayNames = isMobile ? ['S', 'M', 'T', 'W', 'T', 'F', 'S'] : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Mobile: Handle date tap to show tasks
  const handleMobileDateTap = (date: Dayjs) => {
    const dayTasks = getTasksForDate(date);
    setMobileTaskDrawer({ open: true, date, tasks: dayTasks });
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: { xs: 2, md: 3 },
        }}
      >
        <Box>
          <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={600}>
            Calendar
          </Typography>
          {!isMobile && (
            <Typography variant="body2" color="text.secondary">
              Plan and visualize your task deadlines
            </Typography>
          )}
        </Box>
        {!isMobile && (
          <Button
            variant="contained"
            startIcon={<Plus size={16} />}
            onClick={handleCreateTask}
          >
            Add Task
          </Button>
        )}
      </Box>

      {/* Calendar Controls */}
      <Card sx={{ mb: { xs: 2, md: 3 }, borderRadius: { xs: 2, md: 1 } }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: { xs: 1.5, md: 2 },
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 2 }, flex: isMobile ? '1 1 100%' : 'auto', justifyContent: isMobile ? 'space-between' : 'flex-start' }}>
            <IconButton onClick={handlePrev} size={isMobile ? 'small' : 'medium'}>
              <ChevronLeft size={isMobile ? 18 : 20} />
            </IconButton>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight={600} sx={{ textAlign: 'center', minWidth: { xs: 'auto', md: 200 } }}>
              {viewMode === 'month'
                ? currentDate.format(isMobile ? 'MMM YYYY' : 'MMMM YYYY')
                : `${currentDate.startOf('week').format('MMM D')} - ${currentDate.endOf('week').format(isMobile ? 'D' : 'MMM D, YYYY')}`}
            </Typography>
            <IconButton onClick={handleNext} size={isMobile ? 'small' : 'medium'}>
              <ChevronRight size={isMobile ? 18 : 20} />
            </IconButton>
            {!isMobile && (
              <Button variant="outlined" size="small" onClick={handleToday}>
                Today
              </Button>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 0.5, bgcolor: 'grey.100', borderRadius: 1, p: 0.5 }}>
            <IconButton
              onClick={() => setViewMode('month')}
              size="small"
              sx={{
                p: { xs: 0.75, md: 1 },
                bgcolor: viewMode === 'month' ? 'background.paper' : 'transparent',
                color: viewMode === 'month' ? 'primary.main' : 'text.secondary',
                boxShadow: viewMode === 'month' ? 1 : 0,
              }}
            >
              <CalendarDays size={isMobile ? 16 : 18} />
            </IconButton>
            <IconButton
              onClick={() => setViewMode('week')}
              size="small"
              sx={{
                p: { xs: 0.75, md: 1 },
                bgcolor: viewMode === 'week' ? 'background.paper' : 'transparent',
                color: viewMode === 'week' ? 'primary.main' : 'text.secondary',
                boxShadow: viewMode === 'week' ? 1 : 0,
              }}
            >
              <List size={isMobile ? 16 : 18} />
            </IconButton>
            {isMobile && (
              <Button size="small" onClick={handleToday} sx={{ minWidth: 'auto', px: 1.5, fontSize: '0.75rem' }}>
                Today
              </Button>
            )}
          </Box>

          {/* Recurring Tasks Filter */}
          {!isMobile && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={showRecurring}
                  onChange={(e) => setShowRecurring(e.target.checked)}
                  size="small"
                  icon={<Repeat size={16} style={{ opacity: 0.3 }} />}
                  checkedIcon={<Repeat size={16} />}
                />
              }
              label={<Typography variant="caption">Show recurring</Typography>}
              sx={{ ml: 1 }}
            />
          )}
        </Box>

        {/* Week Day Headers */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            bgcolor: 'grey.50',
          }}
        >
          {weekDayNames.map((day) => (
            <Box
              key={day}
              sx={{
                p: { xs: 0.75, md: 1.5 },
                textAlign: 'center',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant={isMobile ? 'caption' : 'subtitle2'} color="text.secondary" fontWeight={500}>
                {day}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Calendar Grid */}
        {viewMode === 'month' ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
            }}
          >
            {getMonthDays().map((day, index) => {
              if (!day) {
                return (
                  <Box
                    key={`empty-${index}`}
                    sx={{
                      minHeight: { xs: 52, md: 120 },
                      bgcolor: 'grey.50',
                      borderBottom: '1px solid',
                      borderRight: (index + 1) % 7 !== 0 ? '1px solid' : 'none',
                      borderColor: 'divider',
                    }}
                  />
                );
              }

              const dayTasks = getTasksForDate(day);
              const dayLeaves = getLeavesForDate(day);
              const isToday = day.isSame(dayjs(), 'day');
              const isSelected = selectedDate?.isSame(day, 'day');

              return (
                <Box
                  key={day.format('YYYY-MM-DD')}
                  onClick={(e) => isMobile ? handleMobileDateTap(day) : handleDateClick(day, e)}
                  sx={{
                    minHeight: { xs: 52, md: 120 },
                    p: { xs: 0.5, md: 1 },
                    cursor: 'pointer',
                    borderBottom: '1px solid',
                    borderRight: (index + 1) % 7 !== 0 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    bgcolor: isSelected ? 'primary.50' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                    '&:active': isMobile ? { bgcolor: 'action.selected' } : {},
                  }}
                >
                  {/* Date Number */}
                  <Box sx={{ display: 'flex', justifyContent: isMobile ? 'center' : 'space-between', mb: { xs: 0, md: 1 } }}>
                    <Typography
                      variant={isMobile ? 'caption' : 'body2'}
                      fontWeight={isToday ? 700 : 400}
                      sx={{
                        width: { xs: 24, md: 28 },
                        height: { xs: 24, md: 28 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        bgcolor: isToday ? 'primary.main' : 'transparent',
                        color: isToday ? 'white' : 'text.primary',
                      }}
                    >
                      {day.date()}
                    </Typography>
                    {!isMobile && (dayTasks.length > 0 || dayLeaves.length > 0) && (
                      <Badge badgeContent={dayTasks.length + dayLeaves.length} color="primary" />
                    )}
                  </Box>

                  {/* Mobile: Task dots indicator */}
                  {isMobile && dayTasks.length > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.25, mt: 0.25 }}>
                      {/* Due-date task dots */}
                      {dayTasks.slice(0, 3).map((task, i) => (
                        <Box
                          key={i}
                          sx={{
                            width: 4,
                            height: 4,
                            borderRadius: '50%',
                            bgcolor: getPriorityColor(task.priority),
                          }}
                        />
                      ))}
                      {dayTasks.length > 5 && (
                        <Typography variant="caption" sx={{ fontSize: '0.5rem', lineHeight: 1 }}>+</Typography>
                      )}
                    </Box>
                  )}

                  {/* Desktop: Leave Requests and Tasks */}
                  {!isMobile && (
                    <>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 0.5 }}>
                        {dayLeaves.map((leave) => {
                          const leaveStart = dayjs(leave.startDate);
                          const leaveEnd = dayjs(leave.endDate);
                          const isFirstDay = day.isSame(leaveStart, 'day');
                          const isLastDay = day.isSame(leaveEnd, 'day');

                          const statusColors = {
                            [LeaveStatus.PENDING]: '#f59e0b',
                            [LeaveStatus.APPROVED]: '#10b981',
                            [LeaveStatus.REJECTED]: '#ef4444',
                            [LeaveStatus.CANCELLED]: '#94a3b8',
                            [LeaveStatus.WITHDRAWN]: '#94a3b8',
                          };

                          return (
                            <Tooltip
                              key={leave.id}
                              title={`${leave.type.replace('_', ' ')} - ${leave.status}`}
                            >
                              <Box
                                sx={{
                                  p: 0.5,
                                  borderRadius: 0.5,
                                  bgcolor: `${statusColors[leave.status]}20`,
                                  borderLeft: isFirstDay ? '3px solid' : 'none',
                                  borderRight: isLastDay ? '3px solid' : 'none',
                                  borderTop: '1px solid',
                                  borderBottom: '1px solid',
                                  borderColor: statusColors[leave.status],
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 600,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    display: 'block',
                                    fontSize: '0.65rem',
                                  }}
                                >
                                  🏖️ {leave.type.split('_')[0]}
                                </Typography>
                              </Box>
                            </Tooltip>
                          );
                        })}
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {dayTasks.slice(0, 3).map((task) => {
                      const parentTask = task.parentTaskId ? taskLookup.get(task.parentTaskId) : null;
                      const hasSubtasks = (task.subtaskIds?.length || 0) > 0;
                      const isBlocked = (task.blockedByTaskIds?.length || 0) > 0;
                      const IssueIcon = task.issueType ? issueTypeConfig[task.issueType]?.icon : null;

                      return (
                        <Tooltip
                          key={task.id}
                          title={
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {task.title}
                              </Typography>
                              {task.isRecurring && (
                                <Typography variant="caption" display="block" sx={{ color: '#c4b5fd' }}>
                                  🔁 Recurring task
                                </Typography>
                              )}
                              {task.category && (
                                <Typography variant="caption" display="block">
                                  Category: {task.category}
                                </Typography>
                              )}
                              {task.issueType && (
                                <Typography variant="caption" display="block">
                                  Type: {issueTypeConfig[task.issueType].label}
                                </Typography>
                              )}
                              {parentTask && (
                                <Typography variant="caption" display="block">
                                  Subtask of: {parentTask.title}
                                </Typography>
                              )}
                              {hasSubtasks && (
                                <Typography variant="caption" display="block">
                                  {task.subtaskIds?.length} subtask(s)
                                </Typography>
                              )}
                              {isBlocked && (
                                <Typography variant="caption" display="block" color="warning.main">
                                  Blocked by {task.blockedByTaskIds?.length} task(s)
                                </Typography>
                              )}
                            </Box>
                          }
                        >
                          <Box
                            onClick={() => handleTaskClick(task.id)}
                            sx={{
                              p: 0.5,
                              borderRadius: 0.5,
                              bgcolor: isBlocked
                                ? alpha('#f59e0b', 0.2)
                                : task.isRecurring
                                ? alpha('#8b5cf6', 0.15)
                                : `${getPriorityColor(task.priority)}20`,
                              borderLeft: '3px solid',
                              borderColor: isBlocked ? '#f59e0b' : task.isRecurring ? '#8b5cf6' : getPriorityColor(task.priority),
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              cursor: 'pointer',
                              '&:hover': {
                                opacity: 0.8,
                              },
                            }}
                          >
                            {/* Icons */}
                            {task.isRecurring && (
                              <Repeat size={10} style={{ color: '#8b5cf6', flexShrink: 0 }} />
                            )}
                            {IssueIcon ? (
                              <IssueIcon
                                size={10}
                                style={{ color: issueTypeConfig[task.issueType!].color, flexShrink: 0 }}
                              />
                            ) : hasSubtasks ? (
                              <Layers size={10} style={{ color: '#8b5cf6', flexShrink: 0 }} />
                            ) : null}
                            {isBlocked && <Lock size={10} style={{ color: '#f59e0b', flexShrink: 0 }} />}

                            {/* Title */}
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                                flex: 1,
                                fontSize: '0.65rem',
                                textDecoration:
                                  task.status === TaskStatus.COMPLETED
                                    ? 'line-through'
                                    : 'none',
                              }}
                            >
                              {task.title}
                            </Typography>

                            {/* Category badge (if space available) */}
                            {task.category && (
                              <Box
                                sx={{
                                  width: 4,
                                  height: 4,
                                  borderRadius: '50%',
                                  bgcolor: getCategoryColor(task.category),
                                  flexShrink: 0,
                                }}
                              />
                            )}
                          </Box>
                        </Tooltip>
                      );
                    })}
                    {dayTasks.length > 3 && (
                      <Typography variant="caption" color="text.secondary">
                        +{dayTasks.length - 3} more
                      </Typography>
                    )}
                      </Box>
                    </>
                  )}
                </Box>
              );
            })}
          </Box>
        ) : (
          // Week View
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
            }}
          >
            {getWeekDays().map((day, index) => {
              const dayTasks = getTasksForDate(day);
              const isToday = day.isSame(dayjs(), 'day');

              return (
                <Box
                  key={day.format('YYYY-MM-DD')}
                  onClick={(e) => handleDateClick(day, e)}
                  sx={{
                    minHeight: 400,
                    p: 1,
                    cursor: 'pointer',
                    borderRight: index < 6 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    bgcolor: isToday ? 'primary.50' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  {/* Date */}
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Typography
                      variant="h4"
                      fontWeight={isToday ? 700 : 400}
                      sx={{
                        color: isToday ? 'primary.main' : 'text.primary',
                      }}
                    >
                      {day.date()}
                    </Typography>
                  </Box>

                  {/* Tasks */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {dayTasks.map((task) => {
                      const parentTask = task.parentTaskId ? taskLookup.get(task.parentTaskId) : null;
                      const hasSubtasks = (task.subtaskIds?.length || 0) > 0;
                      const isBlocked = (task.blockedByTaskIds?.length || 0) > 0;
                      const hasDependencies =
                        (task.dependsOnTaskIds?.length || 0) > 0 ||
                        (task.blockingTaskIds?.length || 0) > 0;
                      const IssueIcon = task.issueType ? issueTypeConfig[task.issueType]?.icon : null;

                      return (
                        <Card
                          key={task.id}
                          variant="outlined"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTaskClick(task.id);
                          }}
                          sx={{
                            p: 1.5,
                            borderLeft: '4px solid',
                            borderColor: isBlocked ? '#f59e0b' : task.isRecurring ? '#8b5cf6' : getPriorityColor(task.priority),
                            bgcolor: isBlocked ? alpha('#f59e0b', 0.1) : task.isRecurring ? alpha('#8b5cf6', 0.08) : 'background.paper',
                            cursor: 'pointer',
                            '&:hover': {
                              boxShadow: 2,
                            },
                          }}
                        >
                          {/* Header with issue type icon */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            {task.isRecurring && (
                              <Repeat size={14} style={{ color: '#8b5cf6' }} />
                            )}
                            {IssueIcon && (
                              <IssueIcon
                                size={14}
                                style={{ color: issueTypeConfig[task.issueType!].color }}
                              />
                            )}
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{
                                flex: 1,
                                textDecoration:
                                  task.status === TaskStatus.COMPLETED ? 'line-through' : 'none',
                              }}
                            >
                              {task.title}
                            </Typography>
                            {isBlocked && (
                              <Tooltip title="Blocked">
                                <Lock size={14} style={{ color: '#f59e0b' }} />
                              </Tooltip>
                            )}
                          </Box>

                          {/* Parent task link */}
                          {parentTask && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <Layers size={12} style={{ color: '#8b5cf6' }} />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                              >
                                Subtask of: {parentTask.title}
                                <ArrowUpRight size={10} />
                              </Typography>
                            </Box>
                          )}

                          {/* Metadata row */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            {/* Start Date */}
                            {task.startDate && (
                              <Tooltip title="Start Date">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Calendar size={12} style={{ color: '#94a3b8' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {dayjs(task.startDate).format('MMM D')}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            )}
                            
                            {/* Due Date */}
                            <Tooltip title="Due Date">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Clock size={12} style={{ color: '#94a3b8' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {dayjs(task.dueDate).format('MMM D')}
                                </Typography>
                              </Box>
                            </Tooltip>

                            {/* Priority */}
                            <PriorityBadge priority={task.priority} size="small" />

                            {/* Category */}
                            {task.category && (
                              <Chip
                                label={task.category}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.65rem',
                                  bgcolor: `${getCategoryColor(task.category)}20`,
                                  color: getCategoryColor(task.category),
                                  fontWeight: 500,
                                }}
                              />
                            )}

                            {/* Subtasks indicator */}
                            {hasSubtasks && (
                              <Tooltip title={`${task.subtaskIds?.length} subtask(s)`}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <FileText size={12} style={{ color: '#8b5cf6' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {task.subtaskIds?.length}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            )}

                            {/* Dependency indicator */}
                            {hasDependencies && (
                              <Tooltip
                                title={`Depends on: ${task.dependsOnTaskIds?.length || 0}, Blocking: ${task.blockingTaskIds?.length || 0}`}
                              >
                                <Link2 size={12} style={{ color: '#3b82f6' }} />
                              </Tooltip>
                            )}
                          </Box>
                        </Card>
                      );
                    })}
                    {dayTasks.length === 0 && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textAlign: 'center', py: 2 }}
                      >
                        No tasks
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Card>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleCreateTask}>
          <Plus size={16} style={{ marginRight: 8 }} />
          Create Task for {selectedDate?.format('MMM D')}
        </MenuItem>
        <MenuItem onClick={handleViewAllTasks}>
          View All Tasks
        </MenuItem>
      </Menu>

      {/* Mobile Task Drawer */}
      <SwipeableDrawer
        anchor="bottom"
        open={mobileTaskDrawer.open}
        onClose={() => setMobileTaskDrawer({ open: false, date: null, tasks: [] })}
        onOpen={() => {}}
        disableSwipeToOpen
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '70vh',
            pb: 'env(safe-area-inset-bottom)',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Handle */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Box sx={{ width: 40, height: 4, bgcolor: 'grey.300', borderRadius: 2 }} />
          </Box>
          
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              {mobileTaskDrawer.date?.format('dddd, MMM D')}
            </Typography>
            <Button size="small" startIcon={<Plus size={14} />} onClick={handleCreateTask}>
              Add
            </Button>
          </Box>
          
          {/* Tasks */}
          {mobileTaskDrawer.tasks.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Calendar size={40} style={{ color: '#94a3b8', marginBottom: 8 }} />
              <Typography variant="body2" color="text.secondary">
                No tasks for this day
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1}>
              {mobileTaskDrawer.tasks.map((task) => (
                <Card
                  key={task.id}
                  onClick={() => {
                    setMobileTaskDrawer({ open: false, date: null, tasks: [] });
                    handleTaskClick(task.id);
                  }}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    borderLeft: '3px solid',
                    borderLeftColor: task.isRecurring ? '#8b5cf6' : getPriorityColor(task.priority),
                    bgcolor: task.isRecurring ? alpha('#8b5cf6', 0.05) : 'background.paper',
                    cursor: 'pointer',
                    '&:active': { bgcolor: 'action.hover' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                        {task.isRecurring && (
                          <Repeat size={14} style={{ color: '#8b5cf6' }} />
                        )}
                        <Typography variant="body2" fontWeight={500} noWrap>
                          {task.title}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <PriorityBadge priority={task.priority} size="small" />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <Clock size={12} style={{ color: '#94a3b8' }} />
                          <Typography variant="caption" color="text.secondary">
                            {dayjs(task.dueDate).format('MMM D')}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <ChevronRightIcon size={18} style={{ color: '#94a3b8' }} />
                  </Box>
                </Card>
              ))}
            </Stack>
          )}
        </Box>
      </SwipeableDrawer>

      {/* Mobile FAB */}
      {isMobile && (
        <Fab
          color="primary"
          onClick={handleCreateTask}
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 16,
            zIndex: 1100,
          }}
        >
          <Plus size={24} />
        </Fab>
      )}
    </Box>
  );
};

export default CalendarPage;
